/**
 * ProductCardSimpleBlock Component
 *
 * Custom BlockNote React component for showcasing individual products or recommendations
 * with images, pricing, descriptions, and CTA buttons in blog content.
 *
 * Features:
 * - Product image upload with automatic 500px variant generation
 * - Product title and MSRP pricing display
 * - Rich text details field with markdown support (HTML converted from markdown)
 * - Optional badge for labels like "Best Value", "Editor's Choice"
 * - 1-2 configurable CTA links opening in new tabs
 * - Progress indicator during image upload
 * - URL validation for links
 * - Edit mode with form fields, view mode with rendered card
 * - Responsive design for mobile and desktop
 *
 * Props (from block.props):
 * - productImage: URL of the product image (required)
 * - title: Product name (required, max 200 chars)
 * - msrp: Price display text (optional, max 100 chars)
 * - details: Rich text product description (required, supports markdown)
 * - badge: Optional label text (max 50 chars)
 * - imageFit: "cover" (default) or "contain" for image display mode
 * - horizontalLayout: Boolean, enables 2-column layout in SSR (image left, text right)
 * - links: Array of 1-2 link objects with url and text
 *
 * Markdown Support:
 * - Users can type markdown syntax in the Product Details field
 * - On blur, markdown is converted to HTML for storage and rendering
 * - Supports detection of existing HTML to prevent double-conversion
 * - If props contain HTML (e.g., from SSR), plain text is extracted for editing
 * - Supported syntax: **bold**, *italic*, [link](url), paragraphs, line breaks
 * - Bullet lists: - item (dash followed by space at start of line)
 * - Numbered lists: 1. item (number and period at start of line)
 *
 * Usage:
 *   This component is automatically rendered when a "productCardSimple" block
 *   is present in BlockNote documents (Pages only, not Documents).
 *
 * Related Files:
 * - app/javascript/controllers/blocks_text_editor_controller.js
 * - app/components/blocknote/blocks/product_card_simple_component.rb
 * - app/controllers/blocknote_images_controller.rb
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import ImageUploadField from "./shared/ImageUploadField.jsx";
import { normalizeWebUrl, sanitizeUrl, sanitizeHtml } from "../../utils/sanitize";
import { markdownToHtml, htmlToMarkdown } from "../../utils/markdown";

/**
 * Link Editor Component
 *
 * Allows editing of a single link with URL and text fields
 */
function LinkEditor({
  link,
  index,
  onChange,
  onRemove,
  canRemove,
  errors,
  touched,
  onTouched,
}) {
  const handleUrlChange = (e) => {
    onChange(index, { ...link, url: e.target.value });
  };

  const handleUrlBlur = () => {
    const normalized = normalizeWebUrl(link.url || "");
    if (normalized !== (link.url || "")) {
      onChange(index, { ...link, url: normalized });
    }
    if (typeof onTouched === "function") onTouched("url");
  };

  const handleTextChange = (e) => {
    onChange(index, { ...link, text: e.target.value });
  };
  const handleTextBlur = () => {
    if (typeof onTouched === "function") onTouched("text");
  };

  return (
    <div className="flex gap-2 mb-2">
      <div className="flex-1">
        <input
          type="url"
          value={link.url || ""}
          onChange={handleUrlChange}
          onBlur={handleUrlBlur}
          placeholder="https://example.com or example.com"
          className={`form-control${errors && errors.url && touched?.url ? " error" : ""}`}
          required
        />
      </div>
      <div className="flex-1">
        <input
          type="text"
          value={link.text || ""}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          placeholder="Link text"
          className={`form-control${errors && errors.text && touched?.text ? " error" : ""}`}
          required
        />
      </div>
      {canRemove && (
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="px-3 py-2 text-red-600 hover:text-red-800"
          aria-label="Remove link"
        >
          ×
        </button>
      )}
    </div>
  );
}

/**
 * Edit Mode Component
 *
 * Renders form fields for editing the product card properties
 */
function ProductCardEditor({ block, onUpdate }) {
  // Helper function to detect if a string contains HTML tags
  const containsHtml = (str) => {
    if (!str) return false;
    return /<\/?[a-z][\s\S]*>/i.test(str);
  };

  // Helper to normalize links array (without adding IDs that could cause issues)
  const normalizeLinks = (links) => {
    if (!Array.isArray(links) || links.length === 0) {
      return [{ url: "", text: "" }];
    }
    return links.map((link) => {
      if (!link || typeof link !== "object") {
        return { url: "", text: "" };
      }
      return { url: link.url || "", text: link.text || "" };
    });
  };

  const [localProps, setLocalProps] = useState({
    productImage: block.props?.productImage || "",
    title: block.props?.title || "",
    msrp: block.props?.msrp || "",
    details: block.props?.details || "",
    badge: block.props?.badge || "",
    imageFit: block.props?.imageFit || "cover",
    horizontalLayout:
      block.props?.horizontalLayout === true ||
      block.props?.horizontalLayout === "true",
    links: normalizeLinks(block.props?.links),
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({
    productImage: false,
    title: false,
    msrp: false,
    details: false,
    links: normalizeLinks(block.props?.links).map(() => ({
      url: false,
      text: false,
    })),
  });
  const [collapsed, setCollapsed] = useState(() => {
    const p = block.props || {};
    const links = Array.isArray(p.links) ? p.links : [];
    const hasContent = Boolean(
      (p.title && p.title.trim()) ||
        (p.msrp && p.msrp.trim()) ||
        (p.details && p.details.trim()) ||
        (p.productImage && p.productImage.trim()) ||
        links.some(
          (l) => l && ((l.url && l.url.trim()) || (l.text && l.text.trim())),
        ),
    );
    return hasContent; // collapse by default when re-editing existing content
  });

  // Initialize markdown state - if props contain HTML, convert back to markdown
  // This allows users to see and edit the original markdown syntax
  const [detailsMarkdown, setDetailsMarkdown] = useState(() => {
    const details = block.props?.details || "";
    return containsHtml(details) ? htmlToMarkdown(details) : details;
  });

  // Sync local state when block.props changes (e.g., after drag/drop reorder)
  // This is necessary because useState initializers only run once on mount
  useEffect(() => {
    setLocalProps({
      productImage: block.props?.productImage || "",
      title: block.props?.title || "",
      msrp: block.props?.msrp || "",
      details: block.props?.details || "",
      badge: block.props?.badge || "",
      imageFit: block.props?.imageFit || "cover",
      horizontalLayout:
        block.props?.horizontalLayout === true ||
        block.props?.horizontalLayout === "true",
      links: normalizeLinks(block.props?.links),
    });
    // Also sync the markdown state
    const details = block.props?.details || "";
    setDetailsMarkdown(
      containsHtml(details) ? htmlToMarkdown(details) : details,
    );
  }, [block.id, block.props]);

  const isValidUrl = (value) => {
    const v = (value || "").trim();
    if (!v) return false;
    const normalized = normalizeWebUrl(v);
    const sanitized = sanitizeUrl(normalized);
    return sanitized !== "#";
  };

  const validate = (props, markdownDetails) => {
    // Defensive: ensure props is an object
    if (!props || typeof props !== "object") {
      return {
        productImage: true,
        title: true,
        msrp: false,
        details: true,
        links: [{ url: true, text: true }],
      };
    }

    const errs = {
      productImage: !(props.productImage && props.productImage.trim()),
      title: !(props.title && props.title.trim()),
      msrp: false, // MSRP is optional
      // Validate against the markdown input (what user sees), not the HTML output
      details: !(markdownDetails && markdownDetails.trim()),
      links: [],
    };
    const links = Array.isArray(props.links) ? props.links : [];
    if (links.length === 0) {
      errs.links = [{ url: true, text: true }];
    } else {
      errs.links = links.map((l) => {
        // Defensive: ensure each link is an object
        if (!l || typeof l !== "object") {
          return { url: true, text: true };
        }
        return {
          url: !isValidUrl(l.url),
          text: !(l.text && l.text.trim()),
        };
      });
    }
    return errs;
  };

  useEffect(() => {
    setErrors(validate(localProps, detailsMarkdown));
  }, [localProps, detailsMarkdown]);

  // Keep touched.links array in sync with number of links
  const linksLength = Array.isArray(localProps.links)
    ? localProps.links.length
    : 0;
  useEffect(() => {
    setTouched((prev) => {
      const desiredLen = linksLength;
      const next = { ...prev };
      const currLen = prev.links?.length || 0;
      if (desiredLen > currLen) {
        next.links = [
          ...(prev.links || []),
          ...Array(desiredLen - currLen)
            .fill(null)
            .map(() => ({ url: false, text: false })),
        ];
      } else if (desiredLen < currLen) {
        next.links = (prev.links || []).slice(0, desiredLen);
      }
      return next;
    });
  }, [linksLength]);

  const markTouched = (field) => setTouched((t) => ({ ...t, [field]: true }));
  const markLinkTouched = (index, field) =>
    setTouched((t) => {
      const links = (t.links || []).slice();
      if (!links[index]) links[index] = { url: false, text: false };
      links[index] = { ...links[index], [field]: true };
      return { ...t, links };
    });

  const handleFieldChange = (field, value) => {
    const newProps = { ...localProps, [field]: value };
    setLocalProps(newProps);
    onUpdate(field, value);
  };

  const handleImageUploaded = (url, variants) => {
    const imageUrl = variants?.["500px"] || url;
    handleFieldChange("productImage", imageUrl);
    markTouched("productImage");
  };

  const handleLinkChange = (index, linkData) => {
    const newLinks = [...localProps.links];
    newLinks[index] = linkData;
    handleFieldChange("links", newLinks);
  };

  const handleAddLink = () => {
    if (localProps.links.length < 2) {
      const newLink = { url: "", text: "" };
      const newLinks = [...localProps.links, newLink];
      handleFieldChange("links", newLinks);
    }
  };

  const handleRemoveLink = (index) => {
    if (localProps.links.length > 1) {
      const newLinks = localProps.links.filter((_, i) => i !== index);
      handleFieldChange("links", newLinks);
    }
  };

  return (
    <div className="product-card-editor w-full p-4 bg-gray-50 border border-gray-200 rounded-lg">
      <div className="space-y-4">
        {/* Product Image Upload */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Product Image *
          </label>
          <ImageUploadField
            currentImageUrl={localProps.productImage}
            onImageUploaded={handleImageUploaded}
            blockType="productCardSimple"
            blockId={block.id}
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/avif"
            maxSize={5 * 1024 * 1024} // 5MB
            hasError={touched.productImage && !!errors.productImage}
            onTouched={() => markTouched("productImage")}
          />
        </div>
        {/* Image Fit */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={(localProps.imageFit || "cover") === "contain"}
              onChange={(e) =>
                handleFieldChange(
                  "imageFit",
                  e.target.checked ? "contain" : "cover",
                )
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-neutral-700">
              Contain Image (w/padding)
            </span>
          </label>
        </div>
        {/* Horizontal Layout */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={localProps.horizontalLayout === true}
              onChange={(e) =>
                handleFieldChange("horizontalLayout", e.target.checked)
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-neutral-700">
              Horizontal Layout (image left, text right)
            </span>
          </label>
        </div>
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Product Title *
          </label>
          <input
            type="text"
            value={localProps.title}
            onChange={(e) =>
              handleFieldChange("title", e.target.value.slice(0, 200))
            }
            onBlur={() => markTouched("title")}
            placeholder="Enter product name"
            className={`form-control${touched.title && errors.title ? " error" : ""}`}
            maxLength="200"
            required
          />
          <span className="text-xs text-neutral-500 mt-1">
            {localProps.title.length}/200 characters
          </span>

          {/* Collapse/Expand Toggle */}
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center"
              aria-expanded={!collapsed}
              aria-controls={`product-card-simple-section-${block.id}`}
            >
              <svg
                className={`w-4 h-4 mr-1 transform transition-transform ${collapsed ? "" : "rotate-90"}`}
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M6 6l6 4-6 4V6z"
                  clipRule="evenodd"
                />
              </svg>
              {collapsed ? "Show details" : "Hide details"}
            </button>
          </div>
        </div>

        {/* Collapsible Details Section */}
        {!collapsed && (
          <div
            id={`product-card-simple-section-${block.id}`}
            className="space-y-4"
          >
            {/* MSRP */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                MSRP (Optional)
              </label>
              <input
                type="text"
                value={localProps.msrp}
                onChange={(e) =>
                  handleFieldChange("msrp", e.target.value.slice(0, 100))
                }
                onBlur={() => markTouched("msrp")}
                placeholder="e.g., MSRP $649"
                className="form-control"
                maxLength="100"
              />
              <span className="text-xs text-neutral-500 mt-1">
                Price display text (e.g., "MSRP $649")
              </span>
            </div>

            {/* Details */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Product Details *
              </label>
              <textarea
                value={detailsMarkdown}
                onChange={(e) => setDetailsMarkdown(e.target.value)}
                onBlur={() => {
                  markTouched("details");
                  const html = markdownToHtml(detailsMarkdown);
                  onUpdate("details", html);
                }}
                placeholder="Enter product description (supports **bold**, *italic*, [links](url), and bullet lists)"
                className={`form-control${touched.details && errors.details ? " error" : ""}`}
                rows="4"
                required
              />
              <span className="text-xs text-neutral-500 mt-1">
                Supports markdown: **bold**, *italic*, [link](url), - bullet
                lists
              </span>
            </div>

            {/* Badge */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Badge (Optional)
              </label>
              <input
                type="text"
                value={localProps.badge}
                onChange={(e) =>
                  handleFieldChange("badge", e.target.value.slice(0, 50))
                }
                placeholder="e.g., Best Value, Editor's Choice"
                className="form-control"
                maxLength="50"
              />
              <span className="text-xs text-neutral-500 mt-1">
                Optional label shown at top-left corner
              </span>
            </div>

            {/* Links */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                CTA Links (1-2 required) *
              </label>
              {(Array.isArray(localProps.links) ? localProps.links : []).map(
                (link, index) => (
                  <LinkEditor
                    key={`${block.id}-link-${index}`}
                    link={link}
                    index={index}
                    onChange={handleLinkChange}
                    onRemove={handleRemoveLink}
                    canRemove={localProps.links.length > 1}
                    errors={errors.links && errors.links[index]}
                    touched={(touched.links && touched.links[index]) || {}}
                    onTouched={(field) => markLinkTouched(index, field)}
                  />
                ),
              )}
              {localProps.links.length < 2 && (
                <button
                  type="button"
                  onClick={handleAddLink}
                  className="mt-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  + Add Link
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * View Mode Component
 *
 * Renders the product card as it will appear to readers
 */
function ProductCardViewer({ block }) {
  const props = block.props || {};
  const links = props.links || [];
  const validLinks = links.filter((link) => link.url && link.text);

  const imageFit =
    (props.imageFit || "cover") === "contain" ? "contain" : "cover";
  const imageClassName =
    imageFit === "contain"
      ? "w-full h-auto min-h-[150px] max-w-[80%] max-h-[240px] object-contain mx-auto !my-0"
      : "w-full h-auto object-cover";

  // Validate required fields (msrp is optional)
  if (!props.productImage || !props.title || !props.details) {
    return (
      <div className="product-card-placeholder p-8 bg-gray-100 rounded-lg text-center text-neutral-500">
        <p>Product Card: Please configure all required fields</p>
      </div>
    );
  }

  return (
    <div className="product-card-simple max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Product Image with Badge */}
        <div className="relative">
          <img
            src={props.productImage}
            alt={props.title}
            className={imageClassName}
          />
          {props.badge && (
            <div className="absolute top-4 left-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
              <svg
                className="w-4 h-4 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              {props.badge}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Title */}
          <h3 className="text-2xl font-bold text-neutral-900 mb-2">
            {props.title}
          </h3>

          {/* MSRP (optional) */}
          {props.msrp && (
            <div className="text-lg font-semibold text-neutral-700 mb-4">
              <span className="uppercase text-sm">Price: </span>
              {props.msrp}
            </div>
          )}

          {/* Details */}
          <div
            className="text-neutral-600 mb-6 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(props.details) }}
          />

          {/* CTA Buttons */}
          {validLinks.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-3">
              {validLinks.map((link, index) => (
                <a
                  key={`${block.id}-view-link-${index}`}
                  href={link.url}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
                  style={{ backgroundColor: "#dc2626" }} // fmt-red color
                >
                  {link.text}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Main ProductCardSimpleBlock Component
 *
 * Manages edit/view mode switching and prop updates
 */
export default function ProductCardSimpleBlock({ block, editor }) {
  const [isEditMode, setIsEditMode] = useState(
    () => editor?.isEditable ?? false,
  );

  // Detect if we're in edit mode or view mode
  useEffect(() => {
    const editMode = editor && editor.isEditable;
    setIsEditMode(editMode);
  }, [editor]);

  // Update block props
  const handleUpdate = useCallback(
    (field, value) => {
      if (!isEditMode) return;

      try {
        // Use editor.getBlock to get the current block state
        // This ensures we have the latest block reference
        const currentBlock = editor.getBlock(block.id);
        if (!currentBlock) {
          console.error("Block not found:", block.id);
          return;
        }

        editor.updateBlock(currentBlock, {
          props: {
            ...currentBlock.props,
            [field]: value,
          },
        });
      } catch (error) {
        console.error("Error updating product card props:", error);
      }
    },
    [editor, block.id, isEditMode],
  );

  if (isEditMode) {
    return <ProductCardEditor block={block} onUpdate={handleUpdate} />;
  }

  return <ProductCardViewer block={block} />;
}
